/**
 * The little dragon that flies past the hero window, drawn and animated in code so its wings,
 * neck and tail can move like a creature's instead of like a stiff cut-out.
 *
 * The dragon is drawn in its own coordinates: facing +x, y down, origin on its back between
 * the shoulders, about 85 units from tail tip to snout. Its wings are rigged in 3D (an arm
 * and a hand that bend at the wrist, flapping round the body's long axis) and projected for a
 * camera slightly above, so a wing reads as thin when level and full when raised.
 *
 * A pass: it comes in from beyond the left of the window, climbs on strong wingbeats, glides
 * down a little with its wings held up, beats again and leaves past the right. Then it stays
 * away a while and comes back from the right, on a slightly different line, and so on.
 */

export interface DragonPose {
  transform: string;
  farWing: string;
  body: string;
  nearWing: string;
}

interface Pt { x: number; y: number; }

interface FlightLine {
  points: Pt[];
  /** Where along the line (0–1) it stops beating its wings and glides, and where it starts again. */
  glide: [number, number];
}

interface SampledLine {
  points: Pt[];
  lengths: number[];
  length: number;
  glide: [number, number];
}

const SCALE = 1;
/** viewBox units per second. */
const SPEED = 54;
const FIRST_PASS_AT = 1.6;
const PAUSES = [13, 17, 15];
/** Twice the x of the window's middle: mirroring x about it turns a line round. */
const MIRROR_X = 560;

/** Each line starts and ends far enough outside the glass that the whole dragon is hidden.
 *  They run left to right; the return trips fly them mirrored about the middle of the window. */
const LINES: FlightLine[] = [
  { points: [{ x: 30, y: 250 }, { x: 150, y: 206 }, { x: 240, y: 178 }, { x: 320, y: 186 }, { x: 400, y: 196 }, { x: 470, y: 172 }, { x: 545, y: 148 }], glide: [.33, .62] },
  { points: [{ x: 30, y: 214 }, { x: 140, y: 182 }, { x: 236, y: 158 }, { x: 330, y: 168 }, { x: 420, y: 180 }, { x: 545, y: 152 }], glide: [.30, .58] },
  { points: [{ x: 30, y: 236 }, { x: 130, y: 214 }, { x: 220, y: 188 }, { x: 300, y: 172 }, { x: 390, y: 184 }, { x: 545, y: 160 }], glide: [.40, .66] }
];

/* —— Wingbeat —— */
const BEAT_PERIOD = .92;
/** Share of a beat spent on the (powerful) downstroke. */
const DOWNSTROKE = .42;
const FLAP_MID = rad(13);
const FLAP_AMPLITUDE = rad(46);
const WRIST_LAG = rad(26);
const GLIDE_ARM = rad(14);
const GLIDE_HAND = rad(6);
/** How far the body rises and falls with each beat, in dragon units. */
const BOB = 2.4;

/* —— Wing planform: x along the body, s out along the span —— */
const SHOULDER: Pt = { x: 3, y: -3 };
const WRIST = { x: 9, s: 15 };
const TIPS = [{ x: -4, s: 44 }, { x: -15, s: 37 }, { x: -23, s: 27 }, { x: -25, s: 16 }];
const WING_ROOT_BACK = { x: -12, s: 0 };
/** How much of the span toward the camera shows as downward on screen (the camera is a little above). */
const VIEW_TILT = .3;

/* —— Spine, from tail tip to the back of the head, with the body's half-depth above and below —— */
const SPINE: { x: number; y: number; top: number; bottom: number }[] = [
  { x: -46, y: 1, top: .3, bottom: .3 },
  { x: -40, y: 2.6, top: .6, bottom: .6 },
  { x: -34, y: 3.4, top: .9, bottom: .9 },
  { x: -28, y: 3.2, top: 1.2, bottom: 1.3 },
  { x: -22, y: 2.4, top: 1.6, bottom: 1.8 },
  { x: -16, y: 1.4, top: 2.2, bottom: 2.6 },
  { x: -10, y: .4, top: 2.8, bottom: 3.6 },
  { x: -4, y: 0, top: 3.2, bottom: 4.5 },
  { x: 2, y: -.4, top: 3.1, bottom: 4.5 },
  { x: 8, y: -1, top: 2.7, bottom: 3.6 },
  { x: 13, y: -2.6, top: 2, bottom: 2.4 },
  { x: 17, y: -4.6, top: 1.7, bottom: 1.9 },
  { x: 21, y: -6.1, top: 1.6, bottom: 1.7 },
  { x: 24.5, y: -6.6, top: 1.7, bottom: 1.8 }
];
const TORSO_START = -10;
const NECK_START = 8;
const SPIKES = [3, 4, 5, 6, 7, 8, 9, 10];

/* —— Fixed parts, in their own coordinates —— */
/** Head and horns; origin at the back of the head, snout toward +x. */
const HEAD = 'M-1,-1.8 C2,-3.1 5,-3.1 8,-2 C10,-1.4 11.6,-.8 12.2,.1 C11,1 9,1.3 7,1.4 C4,1.8 1,2.3 -1,2.1 Z'
  + ' M1.2,-2.4 L-5,-6.4 L3.6,-2.9 Z M4.2,-2.8 L.6,-5.6 L5.8,-2.6 Z';
/** Spade at the tail tip; origin at the tip, the tail running off toward +x. */
const SPADE = 'M1,0 L-4.6,-2.8 L-3.2,0 L-4.6,2.8 Z';
/** Legs tucked up under the belly, in body coordinates. */
const LEGS = 'M-8,3 C-7,7 -10,8.4 -14,8.6 L-17,9.6 L-15.8,7.9 C-13.4,7.3 -12.4,6 -12.6,3.4 Z'
  + ' M5.6,3 C6.6,5.6 5.4,7.1 3,7.7 L1.2,8.8 L1.8,7.1 C3.2,6.1 2.8,4.8 1.8,3.6 Z';

export class DragonFlight {

  private readonly lines = LINES.map(sampleLine);
  private pass = 0;
  private travelled = 0;
  /** Seconds left before the next pass; while positive the dragon is away. */
  private waiting = FIRST_PASS_AT;
  private beat = 0;

  /** A still dragon, gliding across the sky, for the server render and for reduced motion. */
  static restingPose(): DragonPose {
    return pose({ x: 250, y: 172 }, -4, 0, 1);
  }

  /** Moves the flight on by `dt` seconds. Returns null while the dragon is out of sight. */
  advance(dt: number): DragonPose | null {
    if (this.waiting > 0) {
      this.waiting -= dt;
      return null;
    }

    const line = this.lines[this.pass % this.lines.length];
    const u = this.travelled / line.length;
    const [glideFrom, glideTo] = line.glide;
    const flapping = 1 - smoothstep(glideFrom - .06, glideFrom + .04, u) + smoothstep(glideTo - .04, glideTo + .06, u);

    // A touch quicker in the glide, when it trades height for speed.
    this.travelled += SPEED * (1.1 - .1 * flapping) * dt;
    this.beat = (this.beat + dt / BEAT_PERIOD) % 1;

    if (this.travelled >= line.length) {
      this.travelled = 0;
      this.waiting = PAUSES[this.pass % PAUSES.length];
      this.pass++;
      return null;
    }

    const { at, heading } = locate(line, this.travelled);
    return pose(at, heading * .75, this.beat, flapping, this.pass % 2 === 1);
  }
}

/**
 * Draws the dragon at `at`, pitched by `pitch` degrees, at point `beat` (0–1) of a wingbeat, beating
 * its wings by `flapping` (0 = gliding, 1 = full beats). `returning` mirrors the whole picture, so it
 * flies right to left along the mirror image of the line.
 */
function pose(at: Pt, pitch: number, beat: number, flapping: number, returning = false): DragonPose {
  // Stroke angle: 0 with the wings at the top, π at the bottom. The downstroke is the quicker half.
  const stroke = beat < DOWNSTROKE
    ? beat / DOWNSTROKE * Math.PI
    : Math.PI + (beat - DOWNSTROKE) / (1 - DOWNSTROKE) * Math.PI;

  const arm = lerp(GLIDE_ARM, FLAP_MID + FLAP_AMPLITUDE * Math.cos(stroke), flapping);
  // The hand trails the arm: still up when the arm comes down, still down when it goes up.
  const hand = lerp(GLIDE_ARM + GLIDE_HAND, arm + WRIST_LAG * Math.sin(stroke), flapping);
  // It tucks its hands in a little on the way up.
  const reach = 1 - .28 * flapping * Math.max(0, -Math.sin(stroke));

  // Each downstroke lifts the body; the head is held steady against the bob, like a bird's.
  const bob = BOB * flapping * Math.cos(stroke);
  const tailSwing = Math.PI * 2 * beat;
  const nod = -1.6 * flapping * Math.sin(stroke);

  return {
    transform: `translate(${f(returning ? MIRROR_X - at.x : at.x)} ${f(at.y + bob * SCALE)})`
      + `${returning ? ' scale(-1 1)' : ''} rotate(${f(pitch + nod)}) scale(${SCALE})`,
    farWing: wing(arm, hand, reach, -1),
    body: body(bob, tailSwing, flapping),
    nearWing: wing(arm, hand, reach, 1)
  };
}

function wing(arm: number, hand: number, reach: number, side: 1 | -1): string {
  // A point on the arm at span s, or on the hand at planform (x, s), projected to the screen.
  const project = (x: number, up: number, out: number): Pt =>
    ({ x, y: SHOULDER.y - up + side * out * VIEW_TILT });
  const onArm = (x: number, s: number) => project(x, s * Math.sin(arm), s * Math.cos(arm));
  const wristUp = WRIST.s * Math.sin(arm);
  const wristOut = WRIST.s * Math.cos(arm);
  const onHand = (x: number, s: number) => {
    const along = (s - WRIST.s) * reach;
    return project(WRIST.x + (x - WRIST.x) * (.55 + .45 * reach),
      wristUp + along * Math.sin(hand), wristOut + along * Math.cos(hand));
  };

  const root = onArm(SHOULDER.x, 0);
  const wrist = onArm(WRIST.x, WRIST.s);
  const elbow = onArm(WRIST.x + 1.5, WRIST.s * .5);
  const thumb = onHand(WRIST.x + 3.2, WRIST.s + 1.2);
  const knuckle = onHand(WRIST.x - 4, WRIST.s + 16);
  const tips = TIPS.map(t => onHand(t.x, t.s));
  const back = onArm(WING_ROOT_BACK.x, WING_ROOT_BACK.s);

  // The membrane sags between the fingers, toward the wrist.
  const sag = (a: Pt, b: Pt, amount: number): Pt => {
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    return { x: mid.x + (wrist.x - mid.x) * amount, y: mid.y + (wrist.y - mid.y) * amount };
  };

  let d = `M${p(root)} Q${p(elbow)} ${p(wrist)} L${p(thumb)} L${p(wrist)} Q${p(knuckle)} ${p(tips[0])}`;
  for (let i = 1; i < tips.length; i++) {
    d += ` Q${p(sag(tips[i - 1], tips[i], .3))} ${p(tips[i])}`;
  }
  return d + ` Q${p(sag(tips[tips.length - 1], back, .22))} ${p(back)} Z`;
}

function body(bob: number, tailSwing: number, flapping: number): string {
  const spine = SPINE.map(({ x, y }) => {
    let dy = 0;
    if (x < TORSO_START) {
      // A wave runs down the tail behind the wingbeat, bigger toward the tip; slow and lazy in a glide.
      const toTip = (TORSO_START - x) / (TORSO_START - SPINE[0].x);
      const amplitude = lerp(1.2, 3.2, flapping) * Math.pow(toTip, 1.4);
      dy = amplitude * Math.sin(tailSwing - 2.2 * toTip);
    } else if (x > NECK_START) {
      const toHead = (x - NECK_START) / (SPINE[SPINE.length - 1].x - NECK_START);
      dy = -bob * smoothstep(0, 1, toHead);
    }
    return { x, y: y + dy };
  });

  const normals = spine.map((_, i) => {
    const a = spine[Math.max(0, i - 1)];
    const b = spine[Math.min(spine.length - 1, i + 1)];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    // Pointing up, away from the belly.
    return { x: (b.y - a.y) / len, y: -(b.x - a.x) / len };
  });
  const top = spine.map((pt, i) => ({ x: pt.x + normals[i].x * SPINE[i].top, y: pt.y + normals[i].y * SPINE[i].top }));
  const bottom = spine.map((pt, i) => ({ x: pt.x - normals[i].x * SPINE[i].bottom, y: pt.y - normals[i].y * SPINE[i].bottom }));

  let d = `M${p(top[0])}${curveThrough(top)} L${p(bottom[bottom.length - 1])}${curveThrough([...bottom].reverse())} Z`;

  // Spikes along the back, swept toward the tail.
  for (const i of SPIKES) {
    const n = normals[i];
    const t = { x: -n.y, y: n.x };
    const size = lerp(1.1, 2.1, (i - SPIKES[0]) / (SPIKES[SPIKES.length - 1] - SPIKES[0]));
    const base = top[i];
    d += ` M${p({ x: base.x + t.x * size * .7, y: base.y + t.y * size * .7 })}`
      + ` L${p({ x: base.x + n.x * size * 1.3 - t.x * size, y: base.y + n.y * size * 1.3 - t.y * size })}`
      + ` L${p({ x: base.x - t.x * size * .7, y: base.y - t.y * size * .7 })} Z`;
  }

  const head = spine[spine.length - 1];
  const neck = spine[spine.length - 2];
  const tip = spine[0];
  const tail = spine[1];
  return d + ' ' + LEGS
    + ' ' + placed(HEAD, head, Math.atan2(head.y - neck.y, head.x - neck.x) * .6)
    + ' ' + placed(SPADE, tip, Math.atan2(tail.y - tip.y, tail.x - tip.x));
}

/** Moves and turns one of the fixed path strings (absolute M/L/C/Z commands only). */
function placed(path: string, at: Pt, angle: number): string {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return path.replace(/(-?\d*\.?\d+),(-?\d*\.?\d+)/g, (_, xs: string, ys: string) => {
    const x = +xs;
    const y = +ys;
    return p({ x: at.x + x * cos - y * sin, y: at.y + x * sin + y * cos });
  });
}

/** Smooth cubic segments through `points` (a Catmull-Rom spline), starting from points[0]. */
function curveThrough(points: Pt[]): string {
  let d = '';
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    const c1 = { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 };
    const c2 = { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 };
    d += ` C${p(c1)} ${p(c2)} ${p(p2)}`;
  }
  return d;
}

/** Samples a flight line's spline into short straight pieces so the dragon can move along it at an even speed. */
function sampleLine({ points, glide }: FlightLine): SampledLine {
  const samples: Pt[] = [];
  const steps = 48;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    for (let j = 0; j < steps; j++) {
      const t = j / steps;
      samples.push({ x: catmullRom(p0.x, p1.x, p2.x, p3.x, t), y: catmullRom(p0.y, p1.y, p2.y, p3.y, t) });
    }
  }
  samples.push(points[points.length - 1]);

  const lengths = [0];
  for (let i = 1; i < samples.length; i++) {
    lengths.push(lengths[i - 1] + Math.hypot(samples[i].x - samples[i - 1].x, samples[i].y - samples[i - 1].y));
  }
  return { points: samples, lengths, length: lengths[lengths.length - 1], glide };
}

function locate(line: SampledLine, distance: number): { at: Pt; heading: number } {
  let i = 1;
  while (i < line.lengths.length - 1 && line.lengths[i] < distance) {
    i++;
  }
  const a = line.points[i - 1];
  const b = line.points[i];
  const t = (distance - line.lengths[i - 1]) / (line.lengths[i] - line.lengths[i - 1] || 1);
  return {
    at: { x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t) },
    heading: Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI
  };
}

function catmullRom(p0: number, p1: number, p2: number, p3: number, t: number): number {
  return .5 * (2 * p1 + (p2 - p0) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t * t + (3 * p1 - p0 - 3 * p2 + p3) * t * t * t);
}

function smoothstep(from: number, to: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - from) / (to - from)));
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function rad(degrees: number): number {
  return degrees * Math.PI / 180;
}

function f(n: number): string {
  return n.toFixed(2);
}

function p(pt: Pt): string {
  return `${pt.x.toFixed(2)},${pt.y.toFixed(2)}`;
}
